/**
 * React Query hooks for tenant blob storage configuration.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

/**
 * Fetch blob storage config for a tenant.
 */
export function useTenantBlobStorage(tenantId) {
  return useQuery({
    queryKey: ['tenant-blob-storage', tenantId],
    queryFn: async () => {
      const response = await api.get(`/v1/admin/tenants/${tenantId}/blob-storage`);
      return response.data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update blob storage config for a tenant.
 */
export function useUpdateBlobStorage(tenantId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (configData) => {
      const response = await api.put(`/v1/admin/tenants/${tenantId}/blob-storage`, configData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tenant-blob-storage', tenantId]);
    },
  });
}

/**
 * Validate blob storage connection for a tenant.
 */
export function useValidateBlobStorage(tenantId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post(`/v1/admin/tenants/${tenantId}/blob-storage/validate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tenant-blob-storage', tenantId]);
    },
  });
}

/**
 * Provision SROP-managed container for a tenant.
 */
export function useProvisionBlobStorage(tenantId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post(`/v1/admin/tenants/${tenantId}/blob-storage/provision`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tenant-blob-storage', tenantId]);
    },
  });
}
